import { useEffect, useRef, useState } from 'react';
import { MomentInput } from 'moment';
import { localTime, formatStartOf, timePeriod, Popup } from '@helpers';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { useSelector } from 'react-redux';
import { selectSelectedItem } from '@reducers/machines';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { isRangeExactUnit, checkIsMonthSelected, formatDateToView } from '@components/machines/components/timeline/helpers';
import { ColorFunction } from '@styles/colors/types';

export interface UseManageDateHeaderParams {
  C: ColorFunction;
  startTime: MomentInput;
  endTime: MomentInput;
  setStartTime: (start: string) => void;
  setEndTime: (end: string) => void;
  selectRange: ({ start, end }: { start: string; end: string }) => void;
  fetchData: ({ start, end }: { start: string; end: string }) => Promise<void>;
  selectedTimeCounter: number;
  resetOnInitialRender?: boolean;
  isZoomOutDisabled?: boolean;
  rightArrowDisabled?: boolean;
  leftArrowDisabled?: boolean;
  zoomReset?: () => void;
  minTime?: string;
  maxTime?: string;
}

export const ACTION_NAME: Record<TimePeriod, string> = {
  [TimePeriod.DAY]: 'Today',
  [TimePeriod.WEEK]: 'This Week',
  [TimePeriod.MONTH]: 'This Month',
  [TimePeriod.YEAR]: 'This Year',
};

export const DATE_HEADER_HEIGHT = 45;

export const useManageDateHeader = ({
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  selectRange,
  fetchData,
  selectedTimeCounter,
  resetOnInitialRender = true,
  isZoomOutDisabled,
  rightArrowDisabled,
  zoomReset,
  minTime,
  maxTime,
}: UseManageDateHeaderParams) => {
  const [isCustomRange, setIsCustomRange] = useState(false);
  const { timePeriod: selectedTime } = useHandleParams();
  const selectedItem = useSelector(selectSelectedItem) as { machineId?: number; shortName?: string; fullName?: string };
  const zoomTrackingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const zoomDirectionRef = useRef<'in' | 'out'>('in');

  const trackZoomEvent = (direction: 'in' | 'out') => {
    zoomDirectionRef.current = direction;
    if (zoomTrackingTimerRef.current) {
      clearTimeout(zoomTrackingTimerRef.current);
    }
    zoomTrackingTimerRef.current = setTimeout(() => {
      trackEvent(TRACKING_EVENTS.MACHINE_TIMELINE, {
        action: 'zoomed',
        zoomLevel: selectedTime || '',
        machineId: selectedItem?.machineId ? String(selectedItem.machineId) : '',
        machineName: selectedItem?.shortName || selectedItem?.fullName || '',
        zoomDirection: zoomDirectionRef.current,
      });
      zoomTrackingTimerRef.current = null;
    }, 500);
  };

  const handleZoomIn = async () => {
    const amountOfSeconds = localTime(endTime).diff(localTime(startTime), 's') + 1;
    const formattedAmount = amountOfSeconds * 0.2;

    const start = localTime(startTime).add(formattedAmount, 's').format();
    setStartTime(start);
    const end = localTime(endTime).subtract(formattedAmount, 's').format();
    setEndTime(end);
    await fetchData({ start, end });
    trackZoomEvent('in');
  };

  const handleZoomOut = async () => {
    const amountOfSeconds = localTime(endTime).diff(localTime(startTime), 's') + 1;
    const formattedAmount = amountOfSeconds * 0.25;

    let start = localTime(startTime).subtract(formattedAmount, 's');
    const periodStart = minTime ? localTime(minTime) : localTime(startTime).startOf(formatStartOf(selectedTime));
    if (start.isBefore(periodStart)) {
      start = periodStart;
    }

    let end = localTime(endTime).add(formattedAmount, 's');
    const periodEnd = maxTime ? localTime(maxTime) : localTime(endTime).add(1, selectedTime).startOf(formatStartOf(selectedTime));
    if (end.isAfter(periodEnd)) {
      end = periodEnd;
    }

    const formattedStart = start.format();
    const formattedEnd = end.format();

    setStartTime(formattedStart);
    setEndTime(formattedEnd);
    await fetchData({ start: formattedStart, end: formattedEnd });
    trackZoomEvent('out');
  };

  const handleZoomReset = async (shouldAddToEndTime: boolean = true) => {
    if (zoomReset) {
      zoomReset();
      return;
    }

    const start = localTime(startTime).startOf(formatStartOf(selectedTime)).format();
    setStartTime(start);
    const end = !shouldAddToEndTime
      ? localTime(endTime).startOf(formatStartOf(selectedTime)).format()
      : localTime(endTime).add(1, selectedTime).startOf(formatStartOf(selectedTime)).format();
    setEndTime(end);
    await fetchData({ start, end });
    trackZoomEvent('out');
  };

  useEffect(() => {
    if (resetOnInitialRender) {
      handleZoomReset(false);
    }
  }, [selectedTimeCounter, resetOnInitialRender]);

  const setDate = async (d: MomentInput) => {
    const start = localTime(d).startOf('day').format();
    setStartTime(start);
    const end = localTime(d).endOf('day').add(1, 's').format();
    setEndTime(end);
    await fetchData({ start, end });
  };

  const selectCustomRange = ({ start, end }: { start: string; end: string }) => {
    selectRange({ start, end: localTime(end).add(1, 's').format() });
    setIsCustomRange(true);
  };

  const computedIsZoomOutDisabled =
    isZoomOutDisabled ||
    (localTime(startTime).isSame(localTime(startTime).startOf(formatStartOf(selectedTime)), 'm') &&
      localTime(endTime).isSame(localTime(endTime).startOf(formatStartOf(selectedTime)), 'm'));

  const isMonthSelected = checkIsMonthSelected(startTime, endTime);

  const computedRightArrowDisabled = rightArrowDisabled || localTime(endTime).isAfter(localTime());

  const handleLeftArrowPress = () => {
    if (selectedTime === TimePeriod.MONTH && isMonthSelected) {
      selectRange({
        start: localTime(startTime).subtract(1, selectedTime).startOf(formatStartOf(selectedTime)).format(),
        end: localTime(endTime).subtract(2, selectedTime).startOf(formatStartOf(selectedTime)).format(),
      });
      return;
    }

    const range = isRangeExactUnit(startTime, endTime);
    if (range) {
      selectRange({
        start: localTime(startTime).subtract(1, range).format(),
        end: localTime(endTime).subtract(1, range).format(),
      });
    } else {
      const amountOfSeconds = localTime(endTime).diff(localTime(startTime), 's') * 0.25;
      let newStartTime = localTime(startTime).subtract(amountOfSeconds, 's');

      if (minTime && newStartTime.isBefore(minTime)) {
        newStartTime = localTime(minTime);
      }

      selectRange({
        start: newStartTime.format(),
        end: localTime(endTime).subtract(amountOfSeconds, 's').format(),
      });
    }
  };

  const handleRightArrowPress = () => {
    if (selectedTime === TimePeriod.MONTH && isMonthSelected) {
      selectRange({
        start: localTime(startTime).add(1, selectedTime).startOf(formatStartOf(selectedTime)).format(),
        end: localTime(endTime).add(2, selectedTime).startOf(formatStartOf(selectedTime)).format(),
      });
      return;
    }

    const range = isRangeExactUnit(startTime, endTime);
    if (range) {
      selectRange({
        start: localTime(startTime).add(1, range).format(),
        end: localTime(endTime).add(1, range).format(),
      });
    } else {
      const amountOfSeconds = localTime(endTime).diff(localTime(startTime), 's') * 0.25;
      let extraSeconds = 0;

      let endTimeWithAddedPeriod = localTime(endTime).add(amountOfSeconds, 's');

      if (maxTime && endTimeWithAddedPeriod.isAfter(localTime(maxTime))) {
        extraSeconds = endTimeWithAddedPeriod.diff(localTime(maxTime), 's');
        endTimeWithAddedPeriod = localTime(endTime).add(amountOfSeconds - extraSeconds, 's');
      }

      selectRange({
        start: localTime(startTime)
          .add(amountOfSeconds - extraSeconds, 's')
          .format(),
        end: endTimeWithAddedPeriod.format(),
      });
    }
  };

  const handleSelectCurrentPeriod = () => {
    switch (selectedTime) {
      case timePeriod.DAY:
        return selectRange({
          start: localTime().startOf('day').format(),
          end: localTime().add(1, 'day').startOf('day').format(),
        });
      case timePeriod.WEEK:
        return selectRange({
          start: localTime().startOf('isoWeek').format(),
          end: localTime().add(1, 'week').startOf('isoWeek').format(),
        });
      case timePeriod.MONTH:
        return selectRange({
          start: localTime().startOf('month').format(),
          end: localTime().add(1, 'month').startOf('month').format(),
        });
      case timePeriod.YEAR:
        return selectRange({
          start: localTime().startOf('year').format(),
          end: localTime().add(1, 'year').startOf('year').format(),
        });
    }
  };

  const dateLabel = formatDateToView(selectedTime, startTime, endTime, isCustomRange);

  return {
    selectedTime,
    isCustomRange,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleLeftArrowPress,
    handleRightArrowPress,
    handleSelectCurrentPeriod,
    setDate,
    selectCustomRange,
    computedIsZoomOutDisabled,
    computedRightArrowDisabled,
    dateLabel,
  };
};
